import { FolderPlusIcon, PlusCircleIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { PrimaryButton } from '@components/Buttons/PrimaryButtons';
import {
  CallToActionContainer,
  CallToActionItems,
} from '@components/Container';
import { Details, DetailsButton } from '@components/Details';
import { GridHeaderMenuTitle } from '@components/HeaderTitle';
import { TuosLogo } from '@components/Icons';
import { Loading } from '@components/Loading';
import { routes } from '@config/routes';
import { useCustomerGet } from '@hooks/useCustomerGet';
import { useNotification } from '@hooks/useNotification';
import { billingAddressDefaultValue } from '@models/BillingAddress';
import { BillingAddressService } from '@services/BillingAddress';
import { toTitleCase } from '@utils/titleCase';

export const CustomerDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useNotification();
  const { customer: data, isLoading, loadData } = useCustomerGet();

  const [customerAddress, setCustomerAddress] = useState(
    billingAddressDefaultValue
  );

  useEffect(() => {
    if (id) {
      loadData(Number(id));
    }
  }, [id]);

  useEffect(() => {
    if (data.billingID) {
      BillingAddressService.getById(data.billingID).then((result) => {
        if (result.isSuccess && 'data' in result) {
          setCustomerAddress(result.data);
        } else {
          console.error('Error: Failed to get record.', result.errorMessages);
          showError(`${result.errorMessages}`);
        }
      });
    }
  }, [data.billingID]);

  const handleEdit = (rowId: number) => {
    navigate(`${routes.RNC_CUSTOMERS}/${rowId}/edit`, {
      state: { from: location },
    });
  };

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <GridHeaderMenuTitle>
          <div className="lg:text-xl md:text-lg text-sm font-bold uppercase">
            {data.companyName}
          </div>

          <span className="lg:flex md:flex hidden">
            <PrimaryButton
              onClick={() => {
                navigate(`${routes.RNC_CUSTOMERS}/create`, {
                  state: { from: location },
                });
              }}
            >
              Create new customer
            </PrimaryButton>
          </span>

          <button
            onClick={() => {
              navigate(`${routes.RNC_CUSTOMERS}/create`, {
                state: { from: location },
              });
            }}
            className="text-primary-500 lg:hidden md:hidden flex items-center"
          >
            <PlusCircleIcon className="w-6 h-6 md:w-7 md:h-7" />
          </button>
        </GridHeaderMenuTitle>

        <Details>
          <div>
            <CallToActionContainer>
              <CallToActionItems>
                <div className="space-y-3">
                  <div className="flex text-primary-500 lg:text-2xl md:text-lg text-sm items-center lg:space-x-4 md:space-x-3 space-x-2">
                    <FolderPlusIcon className="lg:w-7 lg:h-7 md:w-6 md:h-6 w-5 h-5" />
                    <div className="text-gray-400 font-normal">Create</div>
                  </div>
                </div>

                <div>
                  <DetailsButton onClick={() => handleEdit(data.id)}>
                    Edit
                  </DetailsButton>
                </div>
              </CallToActionItems>
            </CallToActionContainer>
          </div>

          <div className="shadow-md border">
            <div className="lg:p-8 md:p-6 p-4 flex justify-between">
              {/* Desktop */}
              <div className="lg:flex md:hidden hidden items-center">
                <TuosLogo height="100" width="75" />
              </div>
              {/* Tablet */}
              <div className="md:flex lg:hidden hidden items-center">
                <TuosLogo height="75" width="56.25" />
              </div>
              {/* Mobile */}
              <div className="flex lg:hidden md:hidden items-center">
                <TuosLogo height="56.25" width="42.1875" />
              </div>

              <div className="lg:text-xl md:text-base text-sm font-semibold text-end text-gray-500">
                CUSTOMER
                <div className="text-black lg:text-lg md:text-base text-sm">
                  Tuos Development <br />
                  <span className="lg:text-sm md:text-sm text-xs">Canada</span>
                </div>
              </div>
            </div>

            <div className="border-t-2 border-gray-700 lg:py-4 lg:px-8 md:py-4 md:px-6 py-2 px-4">
              <div className="space-y-4">
                <div>
                  <div className="lg:text-base md:text-sm text-sm font-semibold text-gray-500">
                    Primary Contact
                  </div>
                  <div className="lg:text-base md:text-sm text-xs">
                    <span className="uppercase">
                      {data.primaryContact?.firstname}{' '}
                      {data.primaryContact?.lastname}
                    </span>
                    <br />
                    {data.primaryContact?.phone}
                    <br />
                    <span className="lowercase">
                      {data.primaryContact?.email}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="lg:text-base md:text-sm text-sm font-semibold text-gray-500">
                    Billing Address
                  </div>
                  <div className="lg:text-base md:text-sm text-xs">
                    {toTitleCase(`${customerAddress.address1}`)}
                    <br />
                    {toTitleCase(
                      `${customerAddress.address2}${','} ${
                        customerAddress.city
                      }`
                    )}
                    <br />
                    {toTitleCase(
                      `${customerAddress.state}${','} ${
                        customerAddress.country
                      }${','} ${customerAddress.postalCode}`
                    )}
                  </div>
                </div>

                <div>
                  <div className="lg:text-base md:text-sm text-sm font-semibold text-gray-500">
                    Tax Status
                  </div>
                  <div className="lg:text-base md:text-sm text-xs">
                    {data.isTaxExempt ? 'Tax Exempt' : 'Taxable'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Details>
      </>
    );
  }
};
